import { useRef, useState, useEffect } from 'react';
import fixWebmDuration from 'fix-webm-duration';

export function useAudioRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [stream, setStream] = useState(null);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const streamRef = useRef(null);
  const recordingStartTime = useRef(null);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);


  const start = async (mode = 'audio') => {
    const constraints = {
      audio: true,
      video: mode === 'video'
        ? { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } }
        : false,
    };
    const localStream = await navigator.mediaDevices.getUserMedia(constraints);
    streamRef.current = localStream;
    setStream(localStream);

    let mimeType = '';
    if (mode === 'video') {
      const videoTypes = [
        'video/mp4;codecs=avc1',
        'video/mp4;codecs=h264',
        'video/mp4',
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=vp8',
        'video/webm',
      ];
      mimeType = videoTypes.find((t) => MediaRecorder.isTypeSupported(t)) || '';
    } else {
      const audioTypes = [
        'audio/mp4',
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
      ];
      mimeType = audioTypes.find((t) => MediaRecorder.isTypeSupported(t)) || '';
    }

    const options = mimeType
      ? (mode === 'video' ? { mimeType, videoBitsPerSecond: 2500000 } : { mimeType })
      : {};
    mediaRecorder.current = new MediaRecorder(localStream, options);
    chunks.current = [];
    setIsMuted(false);

    mediaRecorder.current.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        chunks.current.push(e.data);
      }
    };
    mediaRecorder.current.start(100);
    recordingStartTime.current = Date.now();
    setIsRecording(true);
  };

  const stop = () =>
    new Promise((resolve) => {
      if (!mediaRecorder.current) {
        resolve(null);
        return;
      }
      mediaRecorder.current.onstop = () => {
        const mimeType = mediaRecorder.current.mimeType || 'audio/webm';
        const rawBlob = new Blob(chunks.current, { type: mimeType });
        const durationMs = recordingStartTime.current
          ? Date.now() - recordingStartTime.current
          : undefined;
        if (durationMs && mimeType.includes('webm')) {
          fixWebmDuration(rawBlob, durationMs, (fixedBlob) => {
            resolve(fixedBlob);
          });
        } else {
          resolve(rawBlob);
        }
        if (mediaRecorder.current.stream) {
          mediaRecorder.current.stream.getTracks().forEach((t) => t.stop());
        }
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        setStream(null);
      };
      mediaRecorder.current.stop();
      setIsRecording(false);
      setIsMuted(false);
    });

  const mute = () => {
    if (mediaRecorder.current?.stream) {
      mediaRecorder.current.stream.getAudioTracks().forEach((track) => {
        track.enabled = false;
      });
      setIsMuted(true);
    }
  };

  const unmute = () => {
    if (mediaRecorder.current?.stream) {
      mediaRecorder.current.stream.getAudioTracks().forEach((track) => {
        track.enabled = true;
      });
      setIsMuted(false);
    }
  };

  return { isRecording, isMuted, stream, start, stop, mute, unmute };
}